// src/owner/pages/OwnerActivityLogs.jsx
import { useState, useEffect, useCallback } from "react";
import OwnerLayout from "../layout/OwnerLayout";
import { API_BASE_URL } from "../../config/api";
import { authHeader } from "../../utils/authHeader";
import Pagination from "../../components/Pagination";

const CATEGORIES = [
  { key: "all",          label: "All Activity",    icon: "📋", color: "#4318FF" },
  { key: "auth",         label: "Login / Logout",  icon: "🔐", color: "#0891b2" },
  { key: "search",       label: "Fraud Searches",  icon: "🔍", color: "#7c3aed" },
  { key: "report",       label: "Reports Filed",   icon: "🚨", color: "#dc2626" },
  { key: "export",       label: "Exports",         icon: "📥", color: "#16a34a" },
  { key: "profile",      label: "Profile Changes", icon: "👤", color: "#d97706" },
  { key: "subscription", label: "Subscription",    icon: "💳", color: "#0891b2" },
];

const ACTION_ICONS = {
  LOGIN:          "🔐", LOGOUT:       "🚪",
  SEARCH_FRAUD:   "🔍", REPORT_FRAUD: "🚨",
  EXPORT_CSV:     "📥", EXPORT_PDF:   "🖨️",
  UPDATE_PROFILE: "👤", UPLOAD_PROOF: "💳",
  SUBSCRIBE:      "✅",
};

const STATUS_STYLE = {
  success: { bg: "#dcfce7", color: "#16a34a" },
  failed:  { bg: "#fee2e2", color: "#dc2626" },
  warning: { bg: "#fef3c7", color: "#d97706" },
};

const OwnerActivityLogs = () => {
  const [logs,      setLogs]      = useState([]);
  const [stats,     setStats]     = useState({});
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [totalPages,setTotalPages]= useState(1);
  const [total,     setTotal]     = useState(0);
  const LIMIT = 20;

  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");

  const fetchLogs = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage, limit: LIMIT,
        ...(category !== "all" && { category }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo   && { dateTo }),
      });
      const res  = await fetch(`${API_BASE_URL}/api/logs/my?${params}`, { headers: authHeader() });
      const data = await res.json();
      setLogs(data.logs || []);
      setStats(data.stats || {});
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setPage(currentPage);
    } catch { }
    setLoading(false);
  }, [category, dateFrom, dateTo]);

  useEffect(() => { setPage(1); fetchLogs(1); }, [fetchLogs]);

  const fmt = (date) => new Date(date).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">My Account</p>
        <h2 className="company-name">Activity Logs</h2>
      </div>

      <div className="owner-dashboard-wrapper">

        {/* Stats cards */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          {CATEGORIES.filter(c => c.key !== "all").map(c => (
            <div key={c.key} onClick={() => setCategory(c.key)}
              style={{ background: category === c.key ? c.color : "#fff", borderRadius: 12, padding: "10px 16px",
                cursor: "pointer", border: `1.5px solid ${category === c.key ? c.color : "#e0e5f2"}`,
                transition: "all 0.2s", minWidth: 90, textAlign: "center" }}>
              <div style={{ fontSize: 18 }}>{c.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: category === c.key ? "#fff" : c.color }}>
                {stats[c.key] || 0}
              </div>
              <div style={{ fontSize: 10, color: category === c.key ? "rgba(255,255,255,0.85)" : "#6b7280", fontWeight: 600 }}>
                {c.label}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={sel}>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 12, color: "#a3aed0" }}>From:</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={sel} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 12, color: "#a3aed0" }}>To:</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={sel} />
            </div>
            {(category !== "all" || dateFrom || dateTo) && (
              <button onClick={() => { setCategory("all"); setDateFrom(""); setDateTo(""); }}
                style={{ padding: "7px 14px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {/* Logs list */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48, color: "#a3aed0" }}>⏳ Loading logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "#a3aed0" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
              <p style={{ fontWeight: 600, color: "#1b2559" }}>No activity logs found</p>
            </div>
          ) : (
            <>
              {logs.map((log, i) => {
                const ss = STATUS_STYLE[log.status] || STATUS_STYLE.success;
                const cat = CATEGORIES.find(c => c.key === log.category);
                return (
                  <div key={log._id} style={{
                    display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 20px",
                    borderBottom: i < logs.length - 1 ? "1px solid #f4f7fe" : "none",
                  }}>
                    {/* Icon */}
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${cat?.color}15`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                      {ACTION_ICONS[log.action] || cat?.icon || "📋"}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>
                          {log.action.replace(/_/g, " ")}
                        </span>
                        <span style={{ fontSize: 10, background: ss.bg, color: ss.color, padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
                          {log.status}
                        </span>
                        <span style={{ fontSize: 10, background: `${cat?.color}15`, color: cat?.color, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>
                          {cat?.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{log.description}</div>
                      <div style={{ fontSize: 11, color: "#a3aed0", marginTop: 4, display: "flex", gap: 12 }}>
                        <span>🕐 {fmt(log.createdAt)}</span>
                        {log.ip && log.ip !== "unknown" && <span>🌐 {log.ip}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: "12px 20px", borderTop: "1px solid #f4f7fe" }}>
                <Pagination currentPage={page} totalPages={totalPages} total={total} limit={LIMIT}
                  onPageChange={p => { setPage(p); fetchLogs(p); }} />
              </div>
            </>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
};

const sel = { height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid #e0e5f2", fontSize: 12, background: "#f9faff", color: "#1b2559", cursor: "pointer", outline: "none" };

export default OwnerActivityLogs;
